import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, ApiError } from './lib/api';
import { useT, type MessageKey } from './lib/i18n';
import {
  TYPE_KEY, labelOf, newFlowKey, typesUsingFlow,
  type CurrentDefinition, type DraftView, type FlowDefinition, type ProblemView, type RegistryDefinition, type StateDefinition
} from './lib/registry';
import { FlowDesigner } from './components/FlowDesigner';
import { templatesOnFlow } from './lib/flowDesign';
import './taxonomy.css';

/**
 * The Flows screen (Veramenta docs/en/40, ported from its back office): the platform's flows, each a
 * Process directive that templates name, edited in one place.
 *
 * <p>A changed flow is saved either as a new flow - the original untouched - or as a change to the
 * flow itself. In Veramenta a change is then weighed against every company's CRM boards
 * (`/flows/impact`) and rolled out one company at a time (`/flows/publish`, `/flows/rollouts`).
 * GeoNexora has no boards and none of those endpoints, so the impact dialog and the rollouts are
 * left out here: a flow change is saved to the registry draft and published with it, as any other
 * change of the taxonomy is.
 */
export function FlowsPage() {
  const { t, locale } = useT();
  const [params, setParams] = useSearchParams();
  const [current, setCurrent] = useState<CurrentDefinition | null>(null);
  const [draft, setDraft] = useState<DraftView | null>(null);
  const [working, setWorking] = useState<RegistryDefinition | null>(null);
  const [base, setBase] = useState<RegistryDefinition | null>(null);
  const [selected, setSelected] = useState<string | null>(params.get('flow'));
  const [choosing, setChoosing] = useState(false);
  const [problems, setProblems] = useState<ProblemView[]>([]);
  const [note, setNote] = useState<{ kind: 'ok' | 'bad'; text: string } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newFor, setNewFor] = useState('');

  const load = useCallback(async () => {
    const [published, saved] = await Promise.all([api.registryDefinition(), api.registryDraft()]);
    setCurrent(published);
    setDraft(saved);
    setWorking(structuredClone(saved?.definition ?? published.definition));
    setBase(saved?.definition ?? published.definition);
    setProblems([]);
  }, []);

  useEffect(() => { void load().catch((e: Error) => setLoadError(e.message)); }, [load]);

  const flows = working?.flows ?? [];
  const flow = flows.find((f) => f.key === selected) ?? flows[0];
  const publishedFlow = (current?.definition.flows ?? []).find((f) => f.key === flow?.key);
  // A flow's designer edits its actions too: anything changed in the definition is something to save.
  const dirty = useMemo(() => working != null && base != null && JSON.stringify(working) !== JSON.stringify(base), [working, base]);
  const templates = useMemo(() => (working?.types ?? []).filter((x) => !x.abstract)
    .sort((a, b) => labelOf(a.labels, locale, a.key).localeCompare(labelOf(b.labels, locale, b.key))), [working, locale]);

  function pick(key: string) {
    setSelected(key);
    setParams({ flow: key }, { replace: true });
    setNote(null);
  }

  function addFlow() {
    const key = newKey.trim();
    if (!TYPE_KEY.test(key) || flows.some((f) => f.key === key) || !working) return;
    // From the flow on screen, as in Veramenta; from a start and an end when there is none yet.
    const states: StateDefinition[] = flow
      ? flow.states.map((s) => ({ ...s, labels: { ...(s.labels ?? {}) } }))
      : [
        { key: 'new', labels: { [locale]: t('tx.starter.state.new') }, colour: '#6b7280', roles: ['start'] },
        { key: 'done', labels: { [locale]: t('tx.starter.state.done') }, colour: '#2f6b46', roles: ['done'] }
      ];
    const made: FlowDefinition = { key, labels: { en: key, [locale]: key }, states };
    setWorking({
      ...working,
      flows: [...(working.flows ?? []), made],
      types: newFor ? working.types.map((x) => {
        if (x.key !== newFor) return x;
        const next = { ...x, flow: key };
        delete next.lifecycle;
        return next;
      }) : working.types
    });
    pick(key);
    setNewKey('');
    setNewFor('');
  }

  /** Saved as the registry draft; published from there (GeoNexora has no rollouts). */
  async function saveDraft(definition: RegistryDefinition) {
    setBusy(true);
    setNote(null);
    setProblems([]);
    try {
      const answer = await api.saveRegistryDraft(definition);
      setDraft(answer);
      setWorking(structuredClone(definition));
      setBase(definition);
      setNote({ kind: 'ok', text: t('taxonomy.saved', { kind: t(`types.kind.${answer.kind}` as MessageKey) }) });
    } catch (e) {
      if (e instanceof ApiError && e.problems.length > 0) {
        setProblems(e.problems);
        setNote({ kind: 'bad', text: t('types.refused', { count: e.problems.length }) });
      } else {
        setNote({ kind: 'bad', text: (e as Error).message });
      }
    } finally {
      setBusy(false);
      setChoosing(false);
    }
  }

  async function publish() {
    setBusy(true);
    try {
      const published = await api.publishRegistry();
      await load();
      setNote({ kind: 'ok', text: t('types.published', { version: published.version }) });
    } catch (e) {
      setNote({ kind: 'bad', text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  async function discardDraft() {
    setBusy(true);
    try {
      await api.discardRegistryDraft();
      await load();
      setNote(null);
    } catch (e) {
      setNote({ kind: 'bad', text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  if (loadError) return <div className="gnx-tax"><div className="banner danger">{loadError}</div></div>;
  if (!working || !current) return <div className="gnx-tax"><div className="empty">{t('taxonomy.loading')}</div></div>;

  return (
    <div className="gnx-tax types-screen flows-screen" data-testid="flows-screen">
      <div className="tx-top">
        <div className="tx-title">
          <h1>{t('flows.title')}</h1>
          <span className="hint">{t('flows.intro', { version: current.version })}</span>
        </div>
      </div>
      <div className="flows-bar">
        <div className="flows-picker" role="tablist" aria-label={t('flows.title')}>
          {flows.map((f) => (
            <button key={f.key} role="tab" aria-selected={f.key === flow?.key} className={f.key === flow?.key ? 'on' : ''}
                    data-testid={`flow-${f.key}`} onClick={() => pick(f.key)} title={f.key}>
              {labelOf(f.labels, locale, f.key)}
              <span className="tx-count" title={t('tx.flow.goesThrough')}>{templatesOnFlow(working, f.key).length}</span>
              {!(current.definition.flows ?? []).some((p) => p.key === f.key) && <span className="badge warn">{t('types.notPublished')}</span>}
            </button>
          ))}
        </div>
        <div className="flows-new">
          <input value={newKey} placeholder={t('flows.newKeyExample')} spellCheck={false} data-testid="new-flow-key" aria-label={t('flows.newKey')}
                 title={t('flows.newHint')} onChange={(e) => setNewKey(e.target.value.trim())}
                 onKeyDown={(e) => { if (e.key === 'Enter') addFlow(); }} />
          <select value={newFor} data-testid="new-flow-for" aria-label={t('tx.new.useFor')} title={t('tx.new.useForHint')}
                  onChange={(e) => setNewFor(e.target.value)}>
            <option value="">{t('flows.forNone')}</option>
            {templates.map((x) => <option key={x.key} value={x.key}>{labelOf(x.labels, locale, x.key)} · {x.key}</option>)}
          </select>
          <button data-testid="new-flow-add" disabled={!TYPE_KEY.test(newKey) || flows.some((f) => f.key === newKey)}
                  onClick={addFlow}>{t('flows.add')}</button>
        </div>
      </div>

      {!flow && <div className="empty tx-canvas" data-testid="flows-none">{t('flows.none')}</div>}
      {flow && (
        <FlowDesigner definition={working} published={current.definition} flowKey={flow.key} onChange={setWorking} />
      )}
      {problems.length > 0 && (
        <ul className="stranded-list tx-problems" data-testid="flows-problems">
          {problems.map((p, i) => <li key={i}><span className="mono">{p.where}</span><span>{p.message}</span></li>)}
        </ul>
      )}

      <footer className="types-foot panel">
        {note && <div className={`banner ${note.kind === 'ok' ? 'info' : 'danger'}`} data-testid="flows-note"><span>{note.text}</span></div>}
        <div className="tab-actions">
          {dirty && <span className="hint">{t('types.unsaved')}</span>}
          {!dirty && draft && <span className="hint">{t('tx.status.draft')}</span>}
          <span className="spacer" />
          {dirty && <button disabled={busy} onClick={() => void load()}>{t('types.discard')}</button>}
          {!dirty && draft && <button className="ghost" disabled={busy} onClick={() => void discardDraft()} data-testid="flows-discard">{t('taxonomy.discard')}</button>}
          <button className={dirty ? 'primary' : ''} data-testid="flows-save" disabled={busy || !dirty}
                  onClick={() => (publishedFlow && flow ? setChoosing(true) : void saveDraft(working))}>
            {t('flows.save')}
          </button>
          <button className={dirty ? '' : 'primary'} data-testid="flows-publish"
                  disabled={busy || dirty || !draft || !(draft.publishable ?? false)} onClick={() => void publish()}>
            {t('taxonomy.publish')}
          </button>
        </div>
      </footer>

      {choosing && flow && publishedFlow && (
        <SaveChoice flow={flow} original={publishedFlow} definition={working} busy={busy}
                    onCancel={() => setChoosing(false)} onSave={(definition) => void saveDraft(definition)} />
      )}
    </div>
  );
}

/**
 * The question a changed flow asks first: a new flow - the original untouched, and the templates
 * chosen here moved to it - or a change to this one, which reaches everything that goes through it.
 */
function SaveChoice({ flow, original, definition, busy, onCancel, onSave }: {
  flow: FlowDefinition;
  original: FlowDefinition;
  definition: RegistryDefinition;
  busy: boolean;
  onCancel: () => void;
  onSave: (definition: RegistryDefinition) => void;
}) {
  const { t, locale } = useT();
  const [how, setHow] = useState<'new' | 'change'>('new');
  const users = typesUsingFlow(definition, flow.key).filter((type) => type.flow === flow.key);
  const [name, setName] = useState(`${labelOf(flow.labels, locale, flow.key)} 2`);
  const [key, setKey] = useState(newFlowKey(definition, `${flow.key}-2`));
  const [moving, setMoving] = useState<Set<string>>(new Set());

  function save() {
    if (how === 'change') {
      onSave(definition);
      return;
    }
    const copy: FlowDefinition = { ...flow, key, labels: { ...(flow.labels ?? {}), [locale]: name, en: name } };
    onSave({
      ...definition,
      flows: [...(definition.flows ?? []).map((f) => (f.key === flow.key ? original : f)), copy],
      types: definition.types.map((type) => (moving.has(type.key) ? { ...type, flow: key } : type))
    });
  }

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" data-testid="flow-save-choice">
      <div className="modal panel">
        <h3>{t('flows.saveHow')}</h3>
        <label className="choice-card">
          <input type="radio" name="how" checked={how === 'new'} data-testid="flow-save-new" onChange={() => setHow('new')} />
          <span><strong>{t('flows.saveAsNew')}</strong><br /><span className="hint">{t('flows.saveAsNewHint')}</span></span>
        </label>
        {how === 'new' && (
          <div className="choice-detail">
            <div className="field-row">
              <label className="field"><span>{t('flows.newName')}</span>
                <input value={name} data-testid="flow-new-name" onChange={(e) => setName(e.target.value)} /></label>
              <label className="field"><span>{t('flows.newKey')}</span>
                <input value={key} className="mono" data-testid="flow-new-key" onChange={(e) => setKey(e.target.value.trim())} /></label>
            </div>
            {users.length > 0 && <p className="hint">{t('flows.moveTypes')}</p>}
            {users.map((type) => (
              <label key={type.key} className="field-check">
                <input type="checkbox" checked={moving.has(type.key)} data-testid={`flow-move-type-${type.key}`}
                       onChange={(e) => setMoving((was) => {
                         const next = new Set(was);
                         if (e.target.checked) next.add(type.key); else next.delete(type.key);
                         return next;
                       })} />
                {labelOf(type.labels, locale, type.key)} <span className="mono hint">{type.key}</span>
              </label>
            ))}
          </div>
        )}
        <label className="choice-card">
          <input type="radio" name="how" checked={how === 'change'} data-testid="flow-save-change" onChange={() => setHow('change')} />
          <span><strong>{t('flows.saveChange')}</strong><br /><span className="hint">{t('flows.saveChangeHint')}</span></span>
        </label>
        <div className="tab-actions">
          <span className="spacer" />
          <button onClick={onCancel} disabled={busy}>{t('common.cancel')}</button>
          <button className="primary" data-testid="flow-save-go" disabled={busy || (how === 'new' && (!TYPE_KEY.test(key) || !name.trim()))}
                  onClick={save}>{t('flows.continue')}</button>
        </div>
      </div>
    </div>
  );
}

export default FlowsPage;
